import { HTML_TAG_GROUPS } from '@src/suite/common/enums/constants';
import { HtmlAttribute, HtmlTag } from '@src/suite/common/enums/enums';

import { NON_BREAKING_SPACE_REGEX } from './util';
import { parseXml } from './xml-utils';

const ignoreTags = HTML_TAG_GROUPS.IGNORE_TAGS;
const blockTags = HTML_TAG_GROUPS.BLOCK_TAGS;

/**
 * Converts pasted HTML into a simplified, sanitized HTML string.
 * Flattens the structure to only use text nodes, span and br tags.
 * No nested elements are preserved.
 */
export const convertHtmlToSimplifiedHtml = (htmlString: string): string => {
    if (!htmlString) return htmlString;
    // First parse the HTML
    const doc = parseXml(htmlString, 'text/html');

    // handle parse error
    if (doc.querySelector(HtmlTag.PARSER_ERROR)) {
        // return original html if parse error
        // but first cleanup all the ignored tags
        return cleanupIgnoredTags(htmlString);
    }

    // Extract all text and formatting into a flat structure
    const fragment = document.createDocumentFragment();
    extractContent(doc.body, fragment);

    // Serialize back to HTML string
    const div = document.createElement(HtmlTag.DIV);
    div.appendChild(fragment);

    // Further simplify the output
    const output = div.innerHTML;
    return cleanupOutput(output);
};

/**
 * Extracts all content from the source node and its descendants,
 * creating a completely flat structure with only text, spans, and br tags.
 */
const extractContent = (sourceNode: Node, targetFragment: DocumentFragment) => {
    if (!sourceNode) return;

    // Process the node tree using a depth-first traversal
    // This allows us to handle the nodes in reading order
    const processNode = (node: Node) => {
        if (node.nodeType === Node.TEXT_NODE) {
            // Clean up text (replace &nbsp; with space, remove control chars)
            // eslint-disable-next-line no-control-regex
            const text = node.textContent?.replace(NON_BREAKING_SPACE_REGEX, ' ')?.replace(/[\x00-\x08\x0B\x0C\x0E-\x1F]/g, '') ?? '';

            if (text.trim()) {
                targetFragment.appendChild(document.createTextNode(text));
            }
            return;
        }

        if (node.nodeType !== Node.ELEMENT_NODE) return;

        const el = node as HTMLElement;
        const tag = el.tagName.toLowerCase() as HtmlTag;

        // Elements to ignore completely
        if (ignoreTags.includes(tag)) return;

        // Handle line breaks
        if (tag === 'br') {
            targetFragment.appendChild(document.createElement(HtmlTag.BR));
            return;
        }

        // For block-level elements, add a line break before (if not at start)
        const isBlockElement = blockTags.includes(tag);

        if (isBlockElement && targetFragment.childNodes.length > 0) {
            targetFragment.appendChild(document.createElement(HtmlTag.BR));
        }

        // Extract formatting from this element
        const formatting = extractFormatting(el);

        // Process all child nodes
        if (el.hasChildNodes()) {
            // If we have formatting to apply, wrap the children in a span
            if (hasFormatting(formatting)) {
                // Create a temporary fragment to hold the children's content
                const tempFragment = document.createDocumentFragment();

                // Process all children
                Array.from(el.childNodes).forEach(child => {
                    processNode(child);
                });

                // If there's content, create a span with the formatting
                if (tempFragment.hasChildNodes()) {
                    const span = document.createElement(HtmlTag.SPAN);
                    applyFormatting(span, formatting);
                    span.appendChild(tempFragment);
                    targetFragment.appendChild(span);
                }
            } else {
                // No formatting, just process children normally
                Array.from(el.childNodes).forEach(child => {
                    processNode(child);
                });
            }
        }

        // For block-level elements, add a line break after
        if (isBlockElement) {
            targetFragment.appendChild(document.createElement(HtmlTag.BR));
        }
    };

    // Start processing from the source node
    Array.from(sourceNode.childNodes).forEach(child => {
        processNode(child);
    });
};

/**
 * Extracts formatting information from an element
 */
const extractFormatting = (el: HTMLElement): FormattingState => {
    const formatting: FormattingState = {};
    const tag = el.tagName.toLowerCase() as HtmlTag;

    // Text formatting based on tag
    if ([HtmlTag.BOLD, HtmlTag.STRONG].includes(tag)) formatting.bold = true;
    if ([HtmlTag.ITALIC, HtmlTag.EM].includes(tag)) formatting.italic = true;
    if (tag === HtmlTag.UNDERLINE) formatting.underline = true;

    // Extract href from links
    if (tag === HtmlTag.A && el.hasAttribute(HtmlAttribute.HREF)) {
        formatting.href = el.getAttribute(HtmlAttribute.HREF) || '';
    }

    // Extract style information - only for basic formatting
    if (el.hasAttribute(HtmlAttribute.STYLE)) {
        const style = el.getAttribute(HtmlAttribute.STYLE) || '';
        if (style.includes('font-weight') && (style.includes('bold') || style.includes('700') || style.includes('800') || style.includes('900'))) {
            formatting.bold = true;
        }
        if (style.includes('font-style') && style.includes('italic')) {
            formatting.italic = true;
        }
        if (style.includes('text-decoration') && style.includes('underline')) {
            formatting.underline = true;
        }
    }

    return formatting;
};

// Interface for tracking formatting state - keep it minimal
interface FormattingState {
    bold?: boolean;
    italic?: boolean;
    underline?: boolean;
    href?: string;
}

// Check if formatting state has any formatting to apply
const hasFormatting = (formatting: FormattingState): boolean => {
    return Object.keys(formatting).length > 0;
};

// Apply formatting to a span element - keep it minimal
const applyFormatting = (span: HTMLElement, formatting: FormattingState) => {
    let style = '';

    if (formatting.bold) style += 'font-weight: bold;';
    if (formatting.italic) style += 'font-style: italic;';
    if (formatting.underline) style += 'text-decoration: underline;';

    if (style) {
        span.setAttribute(HtmlAttribute.STYLE, style);
    }

    // Add data attributes for links
    if (formatting.href) {
        span.setAttribute(HtmlAttribute.DATA_HREF, formatting.href);
    }
};

// Further cleanup the output to remove excessive attributes and styles
const cleanupOutput = (html: string): string => {
    // Replace spans with no style or only data-href with plain text
    html = html.replace(/<span(?:\s+data-href="[^"]*")?>([^<]*)<\/span>/g, '$1');

    // Replace spans with empty style
    html = html.replace(/<span\s+style=""[^>]*>([^<]*)<\/span>/g, '$1');

    // Consolidate consecutive br tags (limit to max 2)
    html = html.replace(/(<br\s*\/?>){3,}/g, '<br><br>');

    // Remove any remaining data attributes except href
    html = html.replace(/\s+data-(?!href)[^=]+="[^"]*"/g, '');

    // Remove any empty spans
    html = html.replace(/<span[^>]*>\s*<\/span>/g, '');

    return html;
};

const cleanupIgnoredTags = (html: string): string => {
    if (!html) return html;

    // Replace ignored tags with empty string
    ignoreTags.forEach(tag => {
        html = html.replace(new RegExp(`<(${tag})[^>]*>s*</(${tag})>`, 'g'), '');
    });

    return html;
};
